import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Download, FileUp, LogOut, Upload } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { importProductsCsv } from "../../services/adminService";
import { getCategoryLabel } from "../../lib/utils-cynos";

const REQUIRED_COLUMNS = [
  "name",
  "qikinkProductId",
  "qikinkSku",
  "designSku",
  "color",
  "size",
  "productSku",
  "storeSku",
  "productCost",
  "sellingPrice",
  "image",
  "category",
  "subCategory",
  "description",
];

const SAMPLE_CSV = [
  REQUIRED_COLUMNS.join(","),
  [
    "Static Mono Tee",
    "62930238",
    "US22",
    "dontfunkw",
    "Black",
    "S",
    "US22",
    "US22-Bk-S",
    "253.64",
    "499",
    "https://example.com/static-mono-black.jpg",
    "men",
    "T-Shirts",
    "Minimal mono print on washed cotton.",
  ].join(","),
  [
    "Static Mono Tee",
    "62930238",
    "US22",
    "dontfunkw",
    "Black",
    "M",
    "US22",
    "US22-Bk-M",
    "253.64",
    "499",
    "https://example.com/static-mono-black.jpg",
    "men",
    "T-Shirts",
    "Minimal mono print on washed cotton.",
  ].join(","),
  [
    "Static Mono Tee",
    "62930238",
    "US22",
    "dontfunkw",
    "White",
    "S",
    "US22",
    "US22-Wh-S",
    "253.64",
    "499",
    "https://example.com/static-mono-white.jpg",
    "men",
    "T-Shirts",
    "Minimal mono print on washed cotton.",
  ].join(","),
].join("\n");

const ProductImport = () => {
  const { admin, checking, logout } = useAuth();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState("");

  if (checking) return <div className="pt-32 text-center font-mono text-zinc-500 uppercase">Authenticating...</div>;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const runImport = async (isPreview) => {
    if (!file) {
      setError("Please choose a CSV file.");
      return;
    }
    setError("");
    setLoading(isPreview ? "preview" : "import");
    try {
      const data = await importProductsCsv(file, isPreview);
      if (isPreview) {
        setPreview(data);
        setResult(null);
      } else {
        setResult(data);
        setPreview(data);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "CSV import failed.");
    } finally {
      setLoading("");
    }
  };

  const onFileChange = (event) => {
    const nextFile = event.target.files?.[0] || null;
    setFile(nextFile);
    setPreview(null);
    setResult(null);
    setError("");
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "cynos-qikink-products-sample.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <main className="min-h-screen bg-cynos-bg" data-testid="admin-product-import-page">
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-white/10 bg-black px-4 py-4 md:px-8">
        <div className="flex items-center gap-6">
          <Link to="/admin" className="font-anton text-2xl uppercase">CYNOS<span className="text-cynos-red">.</span></Link>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Product Import</span>
        </div>
        <button onClick={logout} className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] hover:text-cynos-red">
          <LogOut size={14} /> Logout
        </button>
      </header>

      <div className="px-4 py-8 md:px-8">
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <Link to="/admin" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-cynos-red">
              <ArrowLeft size={14} /> Back to Dashboard
            </Link>
            <h1 className="mt-3 font-anton text-5xl uppercase">Import Qikink CSV</h1>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={downloadSample}
              className="inline-flex items-center gap-2 border border-white/20 px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] hover:border-white"
              data-testid="download-sample-csv"
            >
              <Download size={14} /> Sample CSV
            </button>
            <button
              onClick={() => runImport(true)}
              disabled={!file || loading}
              className="inline-flex items-center gap-2 border border-white/20 px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] hover:border-white disabled:opacity-50"
              data-testid="preview-product-csv"
            >
              <FileUp size={14} /> {loading === "preview" ? "Previewing" : "Preview"}
            </button>
            <button
              onClick={() => runImport(false)}
              disabled={!file || loading || !preview}
              className="inline-flex items-center gap-2 bg-cynos-red px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] text-white hover:bg-red-600 disabled:opacity-50"
              data-testid="import-product-csv"
            >
              <Upload size={14} /> {loading === "import" ? "Importing" : "Import Products"}
            </button>
          </div>
        </div>

        {error && <Alert tone="error">{error}</Alert>}
        {result && (
          <Alert tone="success">
            Imported: {result.products_created} created, {result.products_updated} updated, {result.variants_added} variants added, {result.variants_updated} variants updated, {result.variants_skipped} skipped.
          </Alert>
        )}

        <section className="grid gap-6 lg:grid-cols-[360px_1fr]">
          <div className="border border-white/10 bg-cynos-card p-5">
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">CSV File</span>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={onFileChange}
                className="mt-3 w-full border border-white/15 bg-cynos-surface px-3 py-3 font-mono text-xs"
                data-testid="product-csv-file"
              />
            </label>
            {file && <div className="mt-4 font-mono text-xs uppercase tracking-[0.16em] text-zinc-300">{file.name}</div>}
            <button
              onClick={downloadSample}
              className="mt-4 inline-flex w-full items-center justify-center gap-2 border border-white/20 px-4 py-3 font-mono text-[10px] uppercase tracking-[0.2em] hover:border-white"
              data-testid="download-sample-csv-side"
            >
              <Download size={13} /> Download Sample CSV
            </button>

            <div className="mt-6 border-t border-white/10 pt-5">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Supported Columns</div>
              <div className="mt-3 flex flex-wrap gap-2">
                {REQUIRED_COLUMNS.map((column) => (
                  <span key={column} className="border border-white/10 bg-black/30 px-2 py-1 font-mono text-[10px] text-zinc-400">
                    {column}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            {preview ? <ImportSummary data={preview} /> : <EmptyPreview />}
          </div>
        </section>
      </div>
    </main>
  );
};

const Alert = ({ tone, children }) => {
  const classes = tone === "success"
    ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
    : "border-cynos-red/40 bg-cynos-red/10 text-red-200";
  return <div className={`mb-6 border p-4 font-mono text-xs uppercase tracking-[0.16em] ${classes}`}>{children}</div>;
};

const EmptyPreview = () => (
  <div className="flex min-h-[280px] items-center justify-center border border-dashed border-white/15 bg-cynos-card p-8 text-center">
    <div>
      <FileUp size={30} className="mx-auto text-zinc-600" />
      <div className="mt-4 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Choose a CSV and preview rows</div>
    </div>
  </div>
);

const ImportSummary = ({ data }) => (
  <>
    <div className="grid gap-3 md:grid-cols-4">
      <Metric label="Rows" value={data.rows} />
      <Metric label="Products" value={data.products_detected} />
      <Metric label="Errors" value={data.errors?.length || 0} />
      <Metric label="Warnings" value={data.warnings?.length || 0} />
    </div>

    {!!data.errors?.length && <Messages title="Errors" items={data.errors} tone="error" />}
    {!!data.warnings?.length && <Messages title="Warnings" items={data.warnings} />}

    <div className="overflow-x-auto border border-white/10 bg-cynos-card" data-testid="product-csv-preview">
      <table className="w-full text-sm">
        <thead className="border-b border-white/10 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
          <tr>
            <th className="p-4 text-left">Product</th>
            <th className="p-4 text-left">Qikink</th>
            <th className="p-4 text-left">Colors</th>
            <th className="p-4 text-left">Sizes</th>
            <th className="p-4 text-left">Variants</th>
            <th className="p-4 text-left">Action</th>
          </tr>
        </thead>
        <tbody>
          {data.products?.map((product, index) => (
            <tr key={`${product.name}-${index}`} className="border-b border-white/5">
              <td className="p-4">
                <div className="font-bold uppercase">{product.name}</div>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500">{getCategoryLabel(product.category)}{product.subCategory ? ` / ${product.subCategory}` : ""}</div>
              </td>
              <td className="p-4 font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-400">
                <div>{product.qikinkProductId || "No product id"}</div>
                <div>{product.qikinkSku || "No SKU"}</div>
              </td>
              <td className="p-4 font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-400">{product.colors?.length ? product.colors.join(", ") : "N/A"}</td>
              <td className="p-4 font-mono text-[10px] uppercase tracking-[0.14em] text-zinc-400">{product.sizes?.length ? product.sizes.join(", ") : "N/A"}</td>
              <td className="p-4 font-mono text-xs">{product.variantCount}</td>
              <td className="p-4">
                <span className={`inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.16em] ${product.willUpdate ? "text-amber-300" : "text-emerald-300"}`}>
                  <CheckCircle2 size={13} /> {product.willUpdate ? "Update" : "Create"}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </>
);

const Metric = ({ label, value }) => (
  <div className="border border-white/10 bg-cynos-card p-4">
    <div className="font-anton text-3xl">{value}</div>
    <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
  </div>
);

const Messages = ({ title, items, tone = "warning" }) => {
  const classes = tone === "error" ? "border-cynos-red/30 text-red-200" : "border-amber-400/20 text-amber-200";
  return (
    <div className={`border bg-black/20 p-4 ${classes}`}>
      <div className="font-mono text-[10px] uppercase tracking-[0.2em]">{title}</div>
      <div className="mt-3 grid gap-2">
        {items.slice(0, 20).map((item, index) => (
          <div key={`${item.row}-${index}`} className="font-mono text-[10px] uppercase tracking-[0.14em]">
            Row {item.row}: {item.message}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProductImport;
